/**
 * From a scene element to the row the map's handlers expect. The scene draws the map
 * graph, whose ids are prefixed (`n:` a submission, `b:` an edge-shaped blocker row,
 * `s:` a scenario's start, `l:` a saved loop, `m:` the map); the handlers
 * (useImpactPathHandlers, the blocker and loop modals) take the raw rows the view
 * already holds. Pure, so the mapping is tested without a renderer.
 */

export const uuidOf = (sceneId) => String(sceneId || '').replace(/^[a-z]+:/, '');

/**
 * @param {object} card  a scene card
 * @param {{ pathways: object[], blockers: object[] }} rows  the view's rows
 * @returns {{ kind: 'node'|'blocker'|'root', row: object|null } | null}
 *   `root` is the map's mission card; `node` covers a scenario's start (its starter row).
 */
export function targetOfCard(card, { pathways = [], blockers = [] }) {
  if (!card) return null;
  if (card.kind === 'mission') return { kind: 'root', row: null };
  if (card.kind === 'done') return null;
  const id = uuidOf(card.id);
  if (card.kind === 'start') {
    const starter = pathways.find((p) => (p.scenarioId || p.scenario_id) === id && (p.isScenarioStarter || p.is_scenario_starter));
    return starter ? { kind: 'node', row: starter } : null;
  }
  if (card.id.startsWith('b:')) {
    const row = blockers.find((b) => b.id === id);
    return row ? { kind: 'blocker', row } : null;
  }
  const row = pathways.find((p) => p.id === id);
  return row ? { kind: 'node', row } : null;
}

/** A scene loop → the saved loop row the loop modal takes. */
export function loopRow(sceneLoop, loops = []) {
  const id = uuidOf(sceneLoop?.id);
  return loops.find((l) => l.id === id) || null;
}

/** The scene card ids for a list of row ids (the map's selection, lit on the scene). */
export function sceneIdsFor(ids, scene) {
  if (!ids?.length || !scene) return null;
  const want = new Set(ids);
  return new Set(scene.cards.filter((c) => want.has(uuidOf(c.id))).map((c) => c.id));
}
